import { faMars, faVenus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type React from 'react';
import { cn } from '@/lib/utils';

interface Employee {
	gender?: string;
}

interface GenderProps {
	employee: Employee;
	idx: number;
	updateEmployee: (idx: number, field: string, value: string) => void;
	showValidation?: boolean;
	disabled?: boolean;
}

const Gender = ({
	employee,
	idx,
	updateEmployee,
	showValidation = false,
	disabled = false
}: GenderProps): React.JSX.Element => {
	const isEmpty = !employee.gender || (employee.gender !== 'm' && employee.gender !== 'v');
	const showError = showValidation && isEmpty;

	const baseButtonStyles = cn(
		'flex items-center justify-center h-8 w-9 px-1 py-1.5 border text-xs font-medium transition-colors',
		disabled ? 'cursor-default' : 'cursor-pointer'
	);
	const maleStyles = employee.gender === 'm' ? 'bg-blue-500 text-white border-blue-500' : '';
	const femaleStyles = employee.gender === 'v' ? 'bg-pink-500 text-white border-pink-500' : '';
	const errorStyles = showError ? 'border-destructive' : 'border-input';
	// Only add hover affordances for the interactive (editable) table.
	const maleHover = disabled ? errorStyles : `hover:bg-blue-50 ${errorStyles}`;
	const femaleHover = disabled ? errorStyles : `hover:bg-pink-50 ${errorStyles}`;

	return (
		<div className="flex" role="group" aria-label="Gender selection">
			<input
				type="radio"
				className="sr-only"
				name={`gender-${idx}`}
				id={`m-${idx}`}
				checked={employee.gender === 'm'}
				onChange={() => updateEmployee(idx, 'gender', 'm')}
				disabled={disabled}
				required
			/>
			<label
				className={cn(
					baseButtonStyles,
					'rounded-l-md text-blue-500',
					maleStyles,
					!employee.gender || employee.gender !== 'm' ? maleHover : '',
					showError && employee.gender !== 'm' ? 'border-destructive text-black' : ''
				)}
				htmlFor={`m-${idx}`}
				data-testid={`employee-${idx}-gender-male`}
			>
				<FontAwesomeIcon icon={faMars} className="text-base" aria-hidden="true" />
			</label>
			<input
				type="radio"
				className="sr-only"
				name={`gender-${idx}`}
				id={`v-${idx}`}
				checked={employee.gender === 'v'}
				onChange={() => updateEmployee(idx, 'gender', 'v')}
				disabled={disabled}
				required
			/>
			<label
				className={cn(
					baseButtonStyles,
					'rounded-r-md -ml-px text-pink-500',
					femaleStyles,
					!employee.gender || employee.gender !== 'v' ? femaleHover : '',
					showError && employee.gender !== 'v' ? 'border-destructive' : ''
				)}
				htmlFor={`v-${idx}`}
				data-testid={`employee-${idx}-gender-female`}
			>
				<FontAwesomeIcon icon={faVenus} className="text-base" aria-hidden="true" />
			</label>
		</div>
	);
};

export default Gender;
